import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { Quote } from '../../api';
import { eventEndpoints } from '../../api';
import { Button, Field, Panel } from '../../ui';
import type { PanelProps } from '../Event';
import { INPUT, dollars, humanize } from './shared';
import { dateWords } from '../../terms';

const CHANNELS = ['WEB', 'BOX_OFFICE', 'PHONE', 'GROUP_SALES', 'COMP'];

/** What a sale would post, from the configuration in force right now. Reads only; nothing is sold. */
export function QuotePanel({ detail, onError }: PanelProps) {
  const [channel, setChannel] = useState('WEB');
  const [performanceId, setPerformanceId] = useState(detail.performances[0]?.id ?? '');
  const [zoneId, setZoneId] = useState(detail.price_zones[0]?.id ?? '');
  const [typeId, setTypeId] = useState(detail.ticket_types[0]?.id ?? '');
  const [quantity, setQuantity] = useState('2');
  const [comp, setComp] = useState(false);
  const [result, setResult] = useState<Quote | null>(null);

  const quote = useMutation({
    mutationFn: () =>
      eventEndpoints.quote(detail.event.id, {
        channel,
        items: [{ performance_id: performanceId, price_zone_id: zoneId, ticket_type_id: typeId, quantity: Number(quantity || 1), comp }],
      }),
    onSuccess: setResult,
    onError,
  });

  const ready = performanceId && zoneId && typeId && detail.active_price_table;

  return (
    <div className="space-y-4">
      <Panel title="Quote a sale" description="Runs the same pricing engine a checkout will, against the active price table, fee rules, taxes and accounting rules. Nothing is written.">
        <form
          className="flex flex-wrap items-end gap-4 px-5 py-4"
          onSubmit={(e) => {
            e.preventDefault();
            quote.mutate();
          }}
        >
          <Field label="Channel" htmlFor="q-channel">
            <select id="q-channel" value={channel} onChange={(e) => setChannel(e.target.value)} className={INPUT}>
              {CHANNELS.map((c) => (
                <option key={c} value={c}>
                  {humanize(c)}
                </option>
              ))}
            </select>
          </Field>
          <Field label={`${dateWords().One}`} htmlFor="q-perf">
            <select id="q-perf" value={performanceId} onChange={(e) => setPerformanceId(e.target.value)} className={INPUT}>
              {detail.performances.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.code}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Zone" htmlFor="q-zone">
            <select id="q-zone" value={zoneId} onChange={(e) => setZoneId(e.target.value)} className={INPUT}>
              {detail.price_zones.map((z) => (
                <option key={z.id} value={z.id}>
                  {z.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Ticket type" htmlFor="q-type">
            <select id="q-type" value={typeId} onChange={(e) => setTypeId(e.target.value)} className={INPUT}>
              {detail.ticket_types.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Quantity" htmlFor="q-qty">
            <input id="q-qty" value={quantity} onChange={(e) => setQuantity(e.target.value.replace(/\D/g, ''))} inputMode="numeric" className={`tabular w-16 ${INPUT}`} />
          </Field>
          <label className="flex items-center gap-2 pb-1.5 text-sm">
            <input type="checkbox" checked={comp} onChange={(e) => setComp(e.target.checked)} /> comp
          </label>
          <Button type="submit" variant="primary" disabled={!ready || quote.isPending}>
            {quote.isPending ? 'Pricing…' : 'Quote'}
          </Button>
          {!detail.active_price_table ? <span className="text-xs text-ink-400">Activate a price table first.</span> : null}
        </form>
      </Panel>

      {result ? (
        <Panel title={`Customer pays ${dollars(result.quote.totals.customer_total_cents)}`} description={`Face value ${dollars(result.quote.totals.face_value_cents)} · fees ${dollars(result.quote.totals.fees_cents)} · tax ${dollars(result.quote.totals.tax_cents)}${result.quote.totals.discount_cents ? ` · discounts ${dollars(result.quote.totals.discount_cents)}` : ''}${result.quote.totals.comp_cents ? ` · comps ${dollars(result.quote.totals.comp_cents)}` : ''}`}>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line text-left text-xs font-semibold tracking-wide text-ink-400 uppercase">
                <th className="px-5 py-2">Line</th>
                <th className="px-5 py-2">Unit</th>
                <th className="px-5 py-2 text-right">Amount</th>
                <th className="px-5 py-2">Owner</th>
                <th className="px-5 py-2">Code</th>
                <th className="px-5 py-2">Settlement</th>
                <th className="px-5 py-2">Refundable</th>
              </tr>
            </thead>
            <tbody>
              {result.quote.lines.map((line, i) => (
                <tr key={i} className="border-b border-line last:border-0">
                  <td className="px-5 py-1.5">
                    {humanize(line.line_type)}
                    <span className="ml-1 text-xs text-ink-400">{line.name}{line.included_in_face_value ? ' · in price' : ''}</span>
                  </td>
                  <td className="tabular px-5 py-1.5 text-ink-600">{line.unit ?? 'order'}</td>
                  <td className={`tabular px-5 py-1.5 text-right ${line.amount_cents < 0 ? 'text-bad' : ''}`}>{dollars(line.amount_cents)}</td>
                  <td className="px-5 py-1.5 text-ink-600">{humanize(line.revenue_owner_type)}</td>
                  <td className="px-5 py-1.5 text-xs text-ink-400">{line.accounting_code}</td>
                  <td className="px-5 py-1.5 text-xs text-ink-400">{humanize(line.settlement_behavior)}</td>
                  <td className="px-5 py-1.5 text-ink-600">{line.refundable ? 'yes' : 'no'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="px-5 py-3 text-xs text-ink-400">Priced under price table {String(result.snapshot.price_table_version_id)} and rule set {String(result.snapshot.revenue_rule_set_id)}; a real sale would snapshot the same ids.</p>
        </Panel>
      ) : null}
    </div>
  );
}
